import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {StudentService} from '../../services/student.service';
import {DistrictService} from '../../services/district.service';
import {NgForm} from '@angular/forms';
import {Level, ToastService} from '../../services/toast.service';
import {TypesService} from '../../services/types.service';
import {DistrictTypeDto} from '../../common/dtos/districtTypeDto';
import {DistrictMeetingProblemDto} from '../../common/dtos/districtMeetingProblemDto';
import {NgbNavChangeEvent} from '@ng-bootstrap/ng-bootstrap';
import {MeetingDto} from '@sidr/dto-types';
import {MeetingsProblemDto, MeetingsStrConDto, MeetingsSubProblemDto} from '@sidr/dto-types/dist/types';

@Component({
  selector: `student-meeting`,
  styles: [
    `@media print {
      .showSelected {
        display: block;
      }

      .hideSelected {
        display: block;
      }
    }`,
    `@media screen {
      .showSelected {
        display: block;
      }

      .hideSelected {
        display: block;
      }

      .show {
        display: block;
      }

      .hide {
        display: none;
      }
    }`
  ],
  templateUrl: './student.meeting.component.html',
})
export class StudentMeetingComponent implements OnInit {
  active: any;
  @ViewChild('meetingForm') form: NgForm;
  @Input() studentId: string;
  @Input() districtId: string;
  private _meeting: MeetingDto;
  @Input()
  set meeting(value: MeetingDto) {
    this._meeting = value;
    if (value) {
      this.syncAndSortProblems();
    }
  }

  get meeting(): MeetingDto {
    return this._meeting;
  }

  @Output() meetingChange: EventEmitter<MeetingDto> = new EventEmitter<MeetingDto>();
  @Input() meetings: MeetingDto[];
  @Input() meeting_topics: DistrictTypeDto[] | null;
  @Input() meeting_strcons: DistrictTypeDto[] | null;
  @Input() meeting_problems: DistrictMeetingProblemDto[] | null;
  @Input() meeting_nextsteps: DistrictTypeDto[] | null;
  @Input() editing = false;


  NotificationTitle = 'Meetings';
  problems_expand: any;
  selectedSection = '';
  values: number[];

  constructor(
    private studentService: StudentService,
    private districtService: DistrictService,
    private typesService: TypesService,
    private toastService: ToastService) {
    this.meeting_problems = [];
    this.meeting_strcons = [];
    this.meeting_topics = [];
    this.editing = false;
    this.values = [0, 4, 3, 2, 1];
    this.problems_expand = {};
  }

  onMeetingChange(m: MeetingDto) {
    this.meeting = m;
    this.form.form.markAsDirty();
    this.form.form.markAsTouched();
    this.meetingChange.emit(m);
  }

  copyFrom(m: MeetingDto) {
    this.meeting.problems = m.problems;
    this.meeting.strcons = m.strcons;
    this.meeting.notes = m.notes;
    this.meeting.next_steps = m.next_steps;
    this.meeting.topic = m.topic;
    this.meeting.participant_ids = m.participant_ids;
    this.meeting.other_participants = m.other_participants;
    this.form.form.markAsDirty();
    this.form.form.markAsTouched();
  }

  get canSave(): boolean {
    return !(this.dirty || this.touched);
  }

  get dirty(): boolean {
    return this.form?.form.dirty;
  }

  get touched(): boolean {
    return this.form?.form.touched ?? false;
  }

  problemsExpand(name: string): boolean {
    if (!this.problems_expand[name]) {
      return false;
    }
    return this.problems_expand[name];
  }

  toggleProblemsExpand(name: string) {
    if (!this.problems_expand[name]) {
      this.problems_expand[name] = true;
      return;
    }

    this.problems_expand[name] = !this.problems_expand[name];
  }

  getClass(value: any) {
    if (typeof value === 'string') {
      switch (value) {
        case '4':
          return 'bg-success';
        case '3':
          return 'bg-info';
        case '2':
          return 'bg-warning';
        case '1':
          return 'bg-danger';
        case '0':
          break;
      }
    } else {
      switch (value) {
        case 4:
          return 'bg-success';
        case 3:
          return 'bg-info';
        case 2:
          return 'bg-warning';
        case 1:
          return 'bg-danger';
        case 0:
          break;
      }
    }
  }

  getClassFont(value: any) {
    if (typeof value === 'string') {
      switch (value) {
        case '4':
          return 'text-white';
        case '3':
          return 'text-white';
        case '2':
          return '';
        case '1':
          return 'text-white';
        case '0':
          break;
      }
    } else {
      switch (value) {
        case 4:
          return 'text-white';
        case 3:
          return 'text-white';
        case 2:
          return '';
        case 1:
          return 'text-white';
        case 0:
          break;
      }
    }
  }

  save(form: NgForm) {
    if (this._meeting.last_update === '') {
      delete this._meeting.last_update;
    }

    if (this._meeting.date?.length === 0) {
      delete this._meeting.date;
    }

    this._meeting.problems = this._meeting.problems?.map(p => {
      p.sub_problems = p.sub_problems?.map(sp => {
        sp.int_value = sp?.int_value ?? 0;
        if (sp.int_value && typeof sp.int_value === 'string') {
          sp.int_value = Number(sp.int_value);
        }
        return sp;
      });
      if (p.value && typeof p.value === 'string') {
        p.value = Number(p.value);
      }
      return p;
    }).filter(p => p !== undefined) as MeetingsProblemDto[];

    this._meeting.strcons = this._meeting.strcons?.map(sc => {
      if (sc.strength?.length === 0) {
        delete sc.strength;
      }
      if (sc.concern?.length === 0) {
        delete sc.concern;
      }
      return sc;
    }) ?? [];

    (!this.meeting.id ?
        this.studentService.postMeeting(this.districtId, this.studentId, this.meeting) :
        this.studentService.patchMeeting(this.districtId, this.studentId, this.meeting)
    ).subscribe(r => {
      this.meeting = r;
      this.editing = false;
      form.form.markAsPristine();
      form.form.markAsUntouched();
      this.toastService.AddNotification(Level.SUCCESS, this.NotificationTitle, 'Meeting saved successfully');
    }, () => {
      this.toastService.AddNotification(Level.ERROR, this.NotificationTitle, 'Failed to save meeting');
    });
  }

  addProblem(p: DistrictMeetingProblemDto) {
    this._meeting.problems = this._meeting.problems || [];
    if (this._meeting.problems.findIndex(x => x.name === p.name) > -1) {
      this.toastService.AddNotification(Level.WARNING, this.NotificationTitle, 'Cannot add Problem twice');
      return;
    }

    const newProblem = {
      name: p.name,
      sub_problems: [],
      value: 0
    } as MeetingsProblemDto;
    newProblem.name = p.name;
    newProblem.sub_problems = p.sub_problems?.map(sub => {
      const newSub = {} as MeetingsSubProblemDto;
      newSub.name = sub.name;
      newSub.type = sub.type;
      switch (sub.type) {
        case 'integer':
          newSub.int_value = 0;
          break;
        case 'string':
          newSub.str_value = '';
          break;
      }
      return newSub;
    });

    this._meeting.problems.push(newProblem);
    this.form.form.markAsTouched();
    this.form.form.markAsDirty();
  }

  addStrcon(strcon: DistrictTypeDto) {
    this._meeting.strcons = this._meeting.strcons || [];
    if (this._meeting.strcons.findIndex(x => x.name === strcon.name) > -1) {
      this.toastService.AddNotification(Level.WARNING, this.NotificationTitle, 'Cannot add Strength or Concern twice');
      return;
    }

    const newStrCon = {} as MeetingsStrConDto;
    newStrCon.name = strcon.name;

    this.meeting.strcons.push(newStrCon);
    this.form.form.markAsTouched();
    this.form.form.markAsDirty();
  }

  ngOnInit(): void {
    this.selectedSection = 'information';
  }

  syncAndSortProblems() {
    this._meeting?.problems?.forEach((problem, index) => {
      const districtProblem = this.meeting_problems?.find(p => p.name === problem.name);
      districtProblem?.sub_problems?.forEach(subProblem => {
        // Do we have the subproblem?
        if (!this._meeting.problems[index].sub_problems.find(sp => sp.name === subProblem.name)) {
          // We do not
          const newSub = {} as MeetingsSubProblemDto;
          newSub.name = subProblem.name;
          newSub.type = subProblem.type;
          newSub.str_value = '';
          newSub.int_value = 0;
          this._meeting.problems[index].sub_problems.push(newSub);
        }
      });
    });
  }

  navChange(changeEvent: NgbNavChangeEvent) {
    if (changeEvent.nextId === 'edit') {
      changeEvent.preventDefault();
      this.editing = true;
    }
  }
}
